import { useEffect, useMemo, useState } from "react";
import styled, { css } from "styled-components";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  colorV2,
  cssTemplate,
  media,
  typographyV2,
} from "../../../../../constants/styles";
import Responsable from "../../../../Responsable";
import { useIntersecting } from "../../../../../hooks/useIntersecting";

const RECIPIENT_EMAIL = "collab@safience.com";
const CC_EMAIL_LIST: string[] = [];

function onlyDigits(value: string) {
  return value.replace(/\D/g, "");
}

function formatKoreanPhoneNumber(digitsRaw: string) {
  const digits = onlyDigits(digitsRaw).slice(0, 11);
  if (!digits) return "";

  const isSeoul = digits.startsWith("02");

  if (isSeoul) {
    if (digits.length <= 2) return digits;
    if (digits.length <= 5) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
    if (digits.length <= 9)
      return `${digits.slice(0, 2)}-${digits.slice(2, 5)}-${digits.slice(5)}`;
    return `${digits.slice(0, 2)}-${digits.slice(2, 6)}-${digits.slice(6)}`;
  }

  if (digits.length <= 3) return digits;
  if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  if (digits.length <= 10)
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
}

function formatNumberWithCommas(digitsRaw: string) {
  const digits = onlyDigits(digitsRaw);
  if (!digits) return "";
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

function isCompletePhoneDigits(digitsRaw: string) {
  const digits = onlyDigits(digitsRaw);
  if (!digits) return false;

  const isSeoul = digits.startsWith("02");
  if (isSeoul) return digits.length === 9 || digits.length === 10;
  return digits.length === 10 || digits.length === 11;
}

const contactSchema = z.object({
  companyName: z.string().min(1, "회사명을 입력해주세요."),
  projectName: z.string().min(1, "프로젝트 이름을 입력해주세요."),
  managerName: z.string().min(1, "담당자를 입력해주세요."),
  jobTitle: z.string().min(1, "직책을 입력해주세요."),
  phoneDigits: z
    .string()
    .min(1, "연락처를 입력해주세요.")
    .refine((v) => isCompletePhoneDigits(v), "연락처를 모두 입력해주세요."),
  email: z
    .string()
    .min(1, "이메일을 입력해주세요.")
    .refine((v) => v.includes("@"), "올바른 이메일 형태(@)를 입력해주세요."),
  requestContent: z.string().min(1, "의뢰 내용을 입력해주세요."),
  estimateDigits: z
    .string()
    .optional()
});

type ContactFormValues = z.infer<typeof contactSchema>;

const ScrollOverlayContact = () => {
  const { ref, isIntersecting } = useIntersecting();
  const [interacted, setInteracted] = useState<boolean>(false);

  const [isEstimateFocused, setIsEstimateFocused] = useState(false);

  const defaultValues = useMemo<ContactFormValues>(
    () => ({
      companyName: "",
      projectName: "",
      managerName: "",
      jobTitle: "",
      phoneDigits: "",
      email: "",
      requestContent: "",
      estimateDigits: "",
    }),
    [],
  );

  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema),
    defaultValues,
    mode: "onChange",
  });

  useEffect(() => {
    if (isIntersecting) setInteracted(true);
  }, [isIntersecting]);

  const watchPhoneDigits = watch("phoneDigits");
  const watchEstimateDigits = watch("estimateDigits");
  const formattedPhoneDisplay = formatKoreanPhoneNumber(watchPhoneDigits || "");

  const estimateDisplayValue = isEstimateFocused
    ? onlyDigits(watchEstimateDigits || "")
    : formatNumberWithCommas(watchEstimateDigits || "");

  const onSubmit = (values: ContactFormValues) => {
    const formattedPhone = formatKoreanPhoneNumber(values.phoneDigits);
    const formattedEstimate = values.estimateDigits
      ? `${formatNumberWithCommas(values.estimateDigits)}원`
      : "";

    const subject = `[${values.companyName || "-"}] ${values.projectName || "-"} 견적 문의(슬로그업 홈페이지)`;

    const body = [
      "* 관련 문서 또는 참고 파일이 있다면 첨부 부탁드립니다.",
      "",
      "[회사명]",
      `${values.companyName}`,
      "",
      "[프로젝트 이름]",
      `${values.projectName}`,
      "",
      "[담당자]",
      values.managerName,
      "",
      "[직책]",
      values.jobTitle,
      "",
      "[연락처]",
      formattedPhone,
      "",
      "[이메일]",
      values.email,
      "",
      "[의뢰 내용]",
      values.requestContent,
      "",
      ...(formattedEstimate ? ["[예상 견적]",
        formattedEstimate,] : []),
    ].join("\n");

    const ccEmailList = CC_EMAIL_LIST.join(",") ? `&cc=${CC_EMAIL_LIST.join(",")}` : "";

    const mailtoUrl = `mailto:${RECIPIENT_EMAIL}?subject=${encodeURIComponent(
      subject,
    )}&body=${encodeURIComponent(body)}${ccEmailList}`;

    window.location.assign(mailtoUrl);
  };

  return (
    <Container ref={ref} theme={{ isIntersecting: interacted }}>
      <TextSection>
        <h3>Contact</h3>
        <Responsable
          pc={
            <h2>
              프로젝트 문의나 의뢰가 있으시면
              <br />
              아래의 정보를 입력해주세요
            </h2>
          }
          tablet={
            <h2>
              프로젝트 문의나 의뢰가 있으시면
              <br />
              아래의 정보를 입력해주세요
            </h2>
          }
          mobile={
            <h2>
              프로젝트 문의나 의뢰가
              <br />
              있으시면 아래의 정보를
              <br />
              입력해주세요
            </h2>
          }
        />
      </TextSection>

      <FormWrapper onSubmit={handleSubmit(onSubmit)} noValidate>
        <FormTitle>주식회사 슬로그업</FormTitle>

        <Field style={{ position: 'relative' }}>
          <Label>
            회사명 <RequiredMark>*</RequiredMark>
          </Label>
          <Input
            placeholder="회사 이름을 입력해주세요."
            {...register("companyName")}
            $hasError={!!errors.companyName}
          />
          {errors.companyName?.message && (
            <ErrorText style={{ position: 'absolute', bottom: '-22px' }}>{errors.companyName.message}</ErrorText>
          )}
        </Field>

        <Field style={{ position: 'relative' }}>
          <Label>
            프로젝트 이름 <RequiredMark>*</RequiredMark>
          </Label>
          <Input
            placeholder="프로젝트 이름 또는 문의유형(개발 협업, 비즈니스 협업 등)을 작성해주세요."
            {...register("projectName")}
            $hasError={!!errors.projectName}
          />
          {errors.projectName?.message && (
            <ErrorText style={{ position: 'absolute', bottom: '-22px' }}>{errors.projectName.message}</ErrorText>
          )}
        </Field>

        <Field style={{ position: 'relative' }}>
          <Label>
            담당자 <RequiredMark>*</RequiredMark>
          </Label>
          <Input
            placeholder="담당자를 입력해주세요."
            {...register("managerName")}
            $hasError={!!errors.managerName}
          />
          {errors.managerName?.message && (
            <ErrorText style={{ position: 'absolute', bottom: '-22px' }}>{errors.managerName.message}</ErrorText>
          )}
        </Field>

        <Field style={{ position: 'relative' }}>
          <Label>
            직책 <RequiredMark>*</RequiredMark>
          </Label>
          <Input
            placeholder="직책을 입력해주세요."
            {...register("jobTitle")}
            $hasError={!!errors.jobTitle}
          />
          {errors.jobTitle?.message && (
            <ErrorText style={{ position: 'absolute', bottom: '-22px' }}>{errors.jobTitle.message}</ErrorText>
          )}
        </Field>

        <Field style={{ position: 'relative' }}>
          <Label>
            연락처 <RequiredMark>*</RequiredMark>
          </Label>
          <Controller
            control={control}
            name="phoneDigits"
            render={({ field }) => (
              <Input
                ref={field.ref}
                value={formattedPhoneDisplay}
                onChange={(e) =>
                  field.onChange(onlyDigits(e.target.value).slice(0, 11))
                }
                onBlur={field.onBlur}
                placeholder="연락처를 입력해주세요."
                inputMode="numeric"
                autoComplete="tel"
                $hasError={!!errors.phoneDigits}
              />
            )}
          />
          {errors.phoneDigits?.message && (
            <ErrorText style={{ position: 'absolute', bottom: '-22px' }}>{errors.phoneDigits.message}</ErrorText>
          )}
        </Field>

        <Field style={{ position: 'relative' }}>
          <Label>
            이메일 <RequiredMark>*</RequiredMark>
          </Label>
          <Input
            placeholder="이메일을 입력해주세요."
            type="email"
            autoComplete="email"
            {...register("email")}
            $hasError={!!errors.email}
          />
          {errors.email?.message && <ErrorText style={{ position: 'absolute', bottom: '-22px' }}>{errors.email.message}</ErrorText>}
        </Field>

        <Field style={{ position: 'relative' }}>
          <Label>
            의뢰 내용 <RequiredMark>*</RequiredMark>
          </Label>
          <Textarea
            placeholder="의뢰하실 내용의 간략한 설명을 작성해주세요."
            {...register("requestContent")}
            $hasError={!!errors.requestContent}
          />
          {errors.requestContent?.message && (
            <ErrorText style={{ position: 'absolute', bottom: '-22px' }}>{errors.requestContent.message}</ErrorText>
          )}
        </Field>

        <Field style={{ position: 'relative' }}>
          <Label>예상 견적</Label>
          <EstimateRow>
            <Controller
              control={control}
              name="estimateDigits"
              render={({ field }) => (
                <EstimateInput
                  value={estimateDisplayValue}
                  onChange={(e) => field.onChange(onlyDigits(e.target.value))}
                  onFocus={() => setIsEstimateFocused(true)}
                  onBlur={() => {
                    setIsEstimateFocused(false);
                    field.onBlur();
                  }}
                  placeholder="예상 견적을 입력해주세요."
                  inputMode="numeric"
                  $hasError={!!errors.estimateDigits}
                />
              )}
            />
            <Unit>원</Unit>
          </EstimateRow>
          {errors.estimateDigits?.message && (
            <ErrorText style={{ position: 'absolute', bottom: '-22px' }}>{errors.estimateDigits.message}</ErrorText>
          )}
        </Field>

        <SubmitButton type="submit" disabled={isSubmitting}>
          메일로 문의하기
        </SubmitButton>
      </FormWrapper>
    </Container>
  );
};

export default ScrollOverlayContact;

const Container = styled.div`
  ${(props) => cssTemplate.fadeInOut(props)}
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 140px 48px;
  gap: 100px;
  width: 100%;

  ${media.TABLET} {
    padding: 120px 48px;
  }

  ${media.MOBILE} {
    gap: 60px;
    padding: 80px 24px;
  }
`;

/** 전역 `html`과 동일: Pretendard 우선 */
const formFontFamily = css`
  font-family: "Pretendard", "Fivo Sans Modern", sans-serif;
`;

const TextSection = styled.div`
  display: flex;
  gap: 24px;
  flex-direction: column;
  align-items: center;
  text-align: center;
  color: ${colorV2.GRAY_10};

  h2 {
    ${typographyV2.HEADING_2_B};
  }
  h3 {
    ${typographyV2.BODY_2_M};
  }

  ${media.MOBILE} {
    h2 {
      ${typographyV2.HEADING_3_B};
    }

    h3 {
      ${typographyV2.BODY_4_M};
    }
  }
`;

const FormWrapper = styled.form`
  ${formFontFamily}
  width: 100%;
  max-width: 1024px;
  display: flex;
  flex-direction: column;
  gap: 36px;
  padding: 40px 28px;
  background: ${colorV2.GRAY_10};
  border-radius: 16px;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.18);

  ${media.MOBILE} {
    padding: 20px 16px 24px 16px;
  }
`;

const FormTitle = styled.h1`
  ${typographyV2.HEADING_3_B};
  color: ${colorV2.GRAY_01};
  margin: 0 0 6px 0;

  ${media.MOBILE} {
    ${typographyV2.HEADING_3_B};
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Label = styled.label`
  ${typographyV2.BODY_8_M};
  color: ${colorV2.GRAY_01};
  display: flex;
  align-items: center;
  gap: 6px;
`;

const RequiredMark = styled.span`
  color: #ff3b30;
`;

const ErrorText = styled.p`
  margin: 0;
  font-weight: 500;
  font-size: 12px;
  line-height: 18px;
  color: #ff3b30;
`;

const inputBase = `
  font-family: "Pretendard", "Fivo Sans Modern", sans-serif;
  width: 100%;
  background: transparent;
  border: 1px solid ${colorV2.GRAY_07};
  border-radius: 10px;
  padding: 12px 16px;
  color: ${colorV2.GRAY_01};
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder {
    color: #b8bcc8;
  }

  &:focus {
    border-color: ${colorV2.GRAY_05};
    box-shadow: 0 0 0 4px rgba(0, 0, 0, 0.04);
  }
`;

const Input = styled.input<{ $hasError?: boolean }>`
  ${inputBase}
  ${typographyV2.BODY_8_M};

  ${(p) =>
    p.$hasError
      ? `
    border-color: #ff3b30;
    color: #ff3b30;

    &::placeholder {
      color: #ff3b30;
    }

    &:focus {
      border-color: #ff3b30;
    }
  `
      : ""}
`;

const Textarea = styled.textarea<{ $hasError?: boolean }>`
  ${inputBase}
  ${typographyV2.BODY_8_M};
  min-height: 320px;
  resize: vertical;

  ${(p) =>
    p.$hasError
      ? `
    border-color: #ff3b30;
    color: #ff3b30;

    &::placeholder {
      color: #ff3b30;
    }

    &:focus {
      border-color: #ff3b30;
    }
  `
      : ""}
`;

const EstimateRow = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 12px;
`;

const EstimateInput = styled.input<{ $hasError?: boolean }>`
  ${inputBase}
  ${typographyV2.BODY_8_M};

  ${(p) =>
    p.$hasError
      ? `
    border-color: #ff3b30;
    color: #ff3b30;

    &::placeholder {
      color: #ff3b30;
    }

    &:focus {
      border-color: #ff3b30;
    }
  `
      : ""}
`;

const Unit = styled.span`
  ${typographyV2.BODY_8_M};
  color: ${colorV2.GRAY_05};
`;

const SubmitButton = styled.button`
  ${formFontFamily}
  margin-top: 18px;
  width: 100%;
  border: none;
  border-radius: 12px;
  padding: 16px 18px;
  background: ${colorV2.GRAY_01};
  color: ${colorV2.GRAY_10};
  ${typographyV2.HEADING_11_B};
  cursor: pointer;
  transition: opacity 0.15s ease, transform 0.05s ease;

  &:hover {
    opacity: 0.92;
  }

  &:active {
    transform: translateY(1px);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;
